import { useEffect, type ReactNode } from 'react';

import { DeskChoice } from '../telephony/DeskChoice';
import { FarEnd } from '../telephony/FarEnd';
import { useSoftphone } from '../telephony/useSoftphone';
import { useTelephone } from '../telephony/useTelephone';
import { SoftphoneContext } from './softphoneContext';
import { TelephoneContext } from './telephoneContext';

/**
 * One telephone, above the routes.
 *
 * The `<audio>` element is rendered here rather than by whichever card is showing the call,
 * because swapping the stream on an existing element keeps the chosen speaker and creating
 * a new one per call does not — and a view change must not create one at all.
 *
 * Two halves, both mounted once: the browser telephone, and the office's arrangement of
 * it — which telephone rings, which desk, which call is mine. The question "which desk are
 * you at?" is asked here too, so it is asked once per load whatever view is showing.
 */
export function SoftphoneProvider({ children }: { children: ReactNode }) {
  const phone = useSoftphone();

  const extension =
    phone.registration.state === 'registered' || phone.registration.state === 'reconnecting'
      ? phone.registration.extension
      : null;

  const telephone = useTelephone(extension);

  // What a second caller does, as the administrator set it for this dispatcher.
  const policy = telephone.mine?.whenOnACall === 'SendToOthers' ? 'busy' : 'show';

  useEffect(() => {
    phone.setSecondCalls(policy);
  }, [phone, policy]);

  return (
    <SoftphoneContext.Provider value={phone}>
      <TelephoneContext.Provider value={telephone}>
        {children}
        <FarEnd />
        <DeskChoice />
      </TelephoneContext.Provider>
    </SoftphoneContext.Provider>
  );
}
